import { getCategory } from '@api/interfaces/categoryApi';
import PAGE_SIZES from '@constants/pageSizes';
import useSWRInfinite from 'swr/infinite';
import { useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { ProductCategorySearchPageDataSearchStateDataAxfoodBasicProductViewModelCategoryData } from '@occ/api-client';

const endpoint = '/c';

interface Key {
  endpoint: string;
  q: string;
  categoryPath: string;
  page?: number;
  sort?: string;
}

const fetcher = (key: Key) => {
  const { q, categoryPath, page, sort } = key;
  return getCategory({ q, categoryPath, size: PAGE_SIZES.PAGE.CATEGORY.SIZE.DESKTOP, page, sort });
};

interface Props {
  sort?: string;
  categoryPath: string;
}

const getLastResult = (d: ProductCategorySearchPageDataSearchStateDataAxfoodBasicProductViewModelCategoryData[]) =>
  d && d.length > 0 ? d[d.length - 1] : undefined;

const useCategory = ({ sort, categoryPath }: Props) => {
  const searchParams = useSearchParams();
  const q = searchParams?.get('q') || '';

  const getKey = (page: number): Key | null => {
    if (!categoryPath) return null;
    return { endpoint, q, categoryPath, page, sort };
  };

  const { data = [], setSize, isLoading, mutate } = useSWRInfinite(getKey, fetcher);

  const results = data.filter((r) => r.results).flatMap((r) => r.results);

  const {
    categoryInfo,
    pagination,
    sorts = [],
    breadcrumbs,
    facets = [],
    superCategories,
    subCategories,
  } = getLastResult(data) || {};

  const { currentPage = 0, numberOfPages = 0, totalNumberOfResults = 0 } = pagination || {};

  const hasMorePages = currentPage + 1 < numberOfPages;

  const loadMore = useCallback(async () => {
    if (hasMorePages) {
      try {
        await setSize((s) => s + 1);
      } catch (_) {
        console.log('error');
      }
    }
  }, [hasMorePages, setSize]);

  return {
    products: {
      categoryInfo,
      results,
      pagination,
      sorts,
      hasMorePages,
      facets,
      superCategories,
      subCategories,
      totalNumberOfResults,
      loadMore,
    },
    breadcrumbs,
    isLoading,
    refreshCategories: mutate,
  };
};

export default useCategory;
